import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { IndexType } from "./IndexType";

import { BaseTableDefinitionFactory } from "./BaseTableDefinitionFactory";
import { TableDefinition, BfxColDef } from "./TableDefinition";
import { FilterOrderTypes } from "./FilterTypeEnums";
import { ColumnFilterType } from "./ColumnFilterType";

import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { PrivatizationHelper } from "./orders/PrivatizationHelper";

import { BfxConfigIDRefHelper } from "shared/helper-services/bfx-config-id-ref-helper";

import { CcyPairLookupService } from "../lookup/model/impl/CcyPairLookupService";
import { ClientLookupService } from "../lookup/model/impl/ClientLookupService";
import { AccountLookupService } from "../lookup/model/impl/AccountLookupService";
import { TraderGroupLookupService } from "../lookup/model/impl/TraderGroupLookupService";
import { SalesGroupLookupService } from "../lookup/model/impl/SalesGroupLookupService";
import { UserLookupService } from "../lookup/model/impl/UserLookupService";
import { SystemConfigPromiseService } from "shared/auth-services";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { CustomFieldLookupService } from "../lookup/model/impl/CustomFieldLookupService";

import { SysFeatures } from "./core/SysFeatures";

import { forwardRef, Injectable, Optional } from "@angular/core";
import {
  COLUMN_CLIENT_TRADER,
  COLUMN_DESK_ID,
  COLUMN_INSTRUMENT,
  COLUMN_ORDER_FIXING_SOURCE_CODE,
  COLUMN_ORDER_PRODUCT,
  // COLUMN_CLIENT_CODE,
  COLUMN_ORDER_TYPE,
  COLUMN_SALES_GROUP_ID,
  COLUMN_VALUE_DATE,
  RENDERER_ORDER_TYPE_FUNC,
} from "./OrdersTableDefinitionFactory.constants";
import {
  COLUMN_INTER_INSTRUMENT,
  COLUMN_INTER_DESK_ID,
  COLUMN_INTER_ORDER_PRODUCT,
} from "./InterventionTableDefinitionFactory.constants";
import { TranslateService } from "@ngx-translate/core";
import { AlgoStrategyLookupService } from "../lookup/model/impl/AlgoStrategyLookupService";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ALLOCATION_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { OrderTypeHelper } from "./orders/OrderTypeHelper";
import {
  ITooltipParams,
  RowNode,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { ClientModelPrivatization } from "./privatization/ClientModelPrivatization";
import { AccountModelPrivatization } from "./privatization/AccountModelPrivatization";
import { ClientTraderModelPrivatization } from "./privatization/ClientTraderModelPrivatization";

const DEFAULT_CELL_CLASS: string = "ag-cell-default";

@Injectable()
export class AllocationTableDefinitionFactory extends BaseTableDefinitionFactory {
  protected defaultSortModel: any = [{ colId: "creationDate", sort: "desc" }];
  protected myNdfEnabled: boolean;
  protected myFwdEnabled: boolean;
  protected myNPVEnabled: boolean;

  protected privatizationHelper: PrivatizationHelper;
  public bmarkShortNm: string;

  constructor(
    profileService: ProfilePromiseActionService,
    @Optional() private _sysConfigService: SystemConfigPromiseService,
  ) {
    super(profileService);
  }

  // this override for system configs applicable for blotter definition
  protected getTableCongiPromises(): Array<Promise<any>> {
    return [
      this._sysConfigService.getSystemConfig(SysFeatures.ndfEnabled), // 0
      this._sysConfigService.getSystemConfig(SysFeatures.fwdEnabled), // 1
      this._sysConfigService.getSystemConfig(SysFeatures.bmNPV), // 2
    ];
  }

  // this override to set flags returned by promises
  protected handleTableCongiPromises(values: Array<any>) {
    this.myNdfEnabled = this.valueOn(values[0]);
    this.myFwdEnabled = this.valueOn(values[1]);
    this.myNPVEnabled = this.valueOn(values[2]);
  }

  protected initColdDef(): any {
    return {
      suppressMenu: true,
      cellClass: [DEFAULT_CELL_CLASS],
      editable: false,
      omitFromPicker: false,
      disableInPicker: false,
      resizable: true,
      hide: false,
      minWidth: 20,
    };
  }

  public createDef(): TableDefinition {
    super.createDef();
    let fwdColsVisibleByDefault: boolean = this.myNdfEnabled || this.myFwdEnabled;

    let td = new TableDefinition("allocId", "allocId");

    td.setDefaultSort(1, "desc");

    // Selection col
    td.addColumn(this.createSelectionCol());
    // Allocation id
    td.addColumn(this.createAllocationIdCol());

    td.addColumn(this.createTradeIdCol());
    td.addColumn(this.createDealStateCol());
    td.addColumn(this.createAccountNameCol());
    td.addColumn(this.createAccountCodeCol());
    td.addColumn(this.createIntentCol());
    td.addColumn(this.createCcyCol());
    td.addColumn(this.createOrderAmtCol());

    if (this.myNPVEnabled) {
      td.addColumn(this.createNPVAmtCol());
    }

    td.addColumn(this.createMarketSpotCol());

    td.addColumn(this.createMarketPtsCol());

    td.addColumn(this.createMarketAllInCol());

    td.addColumn(this.createClientSpotCol());

    td.addColumn(this.createClientPtsCol());

    td.addColumn(this.createClientAllInCol());
    td.addColumn(this.createMarkupCol());
    td.addColumn(this.createCounterAmtCol());

    if (this.myNPVEnabled) {
      td.addColumn(this.createNPVCounterAmtCol());
    }

    td.addColumn(this.createFillDateCol());

    td.addColumn(this.createValueDateCol());
    td.addColumn(this.createTraderGroupCol());
    td.addColumn(this.createReqFwdPtsCol());

    return td;
  }

  private createSelectionCol() {
    let selectionColumn: BfxColDef = this.initColdDef();
    selectionColumn.field = "selection";
    selectionColumn.sortingOrder = [];
    selectionColumn.pinned = "left";
    selectionColumn.lockPosition = true;
    selectionColumn.checkboxSelection = true;
    selectionColumn.headerName = "(0)";
    selectionColumn.headerCheckboxSelection = true;
    selectionColumn.headerCheckboxSelectionFilteredOnly = true;
    selectionColumn.sortable = false;
    selectionColumn.suppressMovable = true;
    selectionColumn.resizable = false;
    selectionColumn.suppressMenu = true;
    // enforced width on fixed column
    selectionColumn.cellClass = ["status-icon-cell"];
    selectionColumn.width = this.fourWs;
    selectionColumn.minWidth = this.fourWs;
    selectionColumn.maxWidth = this.fourWs;
    selectionColumn.position = this.position.next();
    selectionColumn.omitFromPicker = true;
    selectionColumn.disableInPicker = true;
    selectionColumn.visibleInEmbeddedMode = true;
    selectionColumn.indexType = IndexType.None;
    selectionColumn.columnFilterType = ColumnFilterType.NONE;
    selectionColumn.valueGetter = () => "";
    selectionColumn.cellRenderer = function (params) {
      return params.value != null ? params.value : "";
    };
    selectionColumn.tooltipValueGetter = () => "";
    return selectionColumn;
  }

  private createAllocationIdCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Alloc ID";
    col.headerTooltip = "Allocation ID";
    col.field = "allocId";
    col.hide = true;
    col.indexType = IndexType.String;
    col.minWidth = BfxConfigIDRefHelper.getAllocationRefWs();
    col.width = BfxConfigIDRefHelper.getAllocationRefWs();
    col.columnFilterType = ColumnFilterType.NONE;
    col.valueFormatter = BfxConfigIDRefHelper.allocationRefValueFormatter;
    col.getQuickFilterText = BfxConfigIDRefHelper.allocationRefValueFormatter;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  private createTradeIdCol() {
    let tradeIdCol: BfxColDef = this.initColdDef();
    tradeIdCol.headerName = "Trade ID";
    tradeIdCol.field = "dealId";
    tradeIdCol.indexType = IndexType.String;
    tradeIdCol.columnFilterType = ColumnFilterType.NONE;
    tradeIdCol.minWidth = BfxConfigIDRefHelper.getDealRefWs();
    tradeIdCol.width = BfxConfigIDRefHelper.getDealRefWs();
    tradeIdCol.valueFormatter = BfxConfigIDRefHelper.dealRefValueFormatter;
    tradeIdCol.getQuickFilterText = BfxConfigIDRefHelper.dealRefValueFormatter;
    if (!this.restoreFromConfig(tradeIdCol)) {
      tradeIdCol.position = this.position.next();
    }
    return tradeIdCol;
  }

  private createDealStateCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Deal State";
    col.field = "dealState";
    col.indexType = IndexType.String;
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 20;
    col.width = 80;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  private createAccountCodeCol() {
    // Acounts have already been privatised by this point
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Account ID";
    col.field = "accountCode";
    col.indexType = IndexType.String;
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 70;
    col.width = 130;
    col.hide = true;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  private createAccountNameCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Account Name";
    col.field = "accountName";
    col.indexType = IndexType.String;
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 70;
    col.width = 130;
    col.hide = true;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  private createIntentCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Dir";
    col.headerTooltip = "Direction (Dealt)";
    col.field = "clientIntent";
    col.cellClass = "blotter_center_align_col intent_col";
    col.indexType = IndexType.String;
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 48;
    col.width = 48;
    col.cellRenderer = "intentRenderer";
    col.valueGetter = (params) => params?.data?.dir ?? "-";

    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  private createCcyCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Ccy";
    col.headerTooltip = "Dealt Ccy";
    col.field = "dealtCcy";
    col.cellClass = "blotter_center_align_col";
    col.indexType = IndexType.String;
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 48;
    col.width = 48;
    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  private createOrderAmtCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Order Amt";
    col.headerTooltip = "Order Amount (Dealt)";
    col.field = "orderAmt";
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 70;
    col.width = 120;
    col.comparator = this.createNumberComparator(col.field);
    return col;
  }

  private createNPVAmtCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "NPV Order Amt";
    col.headerTooltip = "NPV Order Amount (Dealt)";
    col.field = "npvAmt";
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 70;
    col.width = 120;
    col.comparator = this.createNumberComparator(col.field);
    return col;
  }

  private createMarketSpotCol() {
    return this.createOrderRateCol("Market Spot", "marketSpot");
  }

  private createMarketPtsCol() {
    return this.createOrderRateCol("Market Pts", "marketPts");
  }

  private createMarketAllInCol() {
    return this.createOrderRateCol("Market All-In", "marketAllIn");
  }

  private createClientSpotCol() {
    return this.createOrderRateCol("Client Spot", "clientSpot");
  }

  private createClientPtsCol() {
    return this.createOrderRateCol("Client Pts", "clientPts");
  }

  private createClientAllInCol() {
    return this.createOrderRateCol("Client All-In", "clientAllIn");
  }

  private createMarkupCol() {
    return this.createOrderRateCol("Markup", "markup");
  }

  private createReqFwdPtsCol() {
    const col = this.createOrderRateCol("Req Forward Pts", "reqFwdPoints");
    col.headerTooltip = "Request Forward Pts";
    return col;
  }

  private createCounterAmtCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Counter Amt";
    col.headerTooltip = "Counter Amount";
    col.field = "counterAmt";
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 70;
    col.width = 120;
    col.comparator = this.createNumberComparator(col.field);
    return col;
  }

  private createNPVCounterAmtCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "NPV Counter Amt";
    col.headerTooltip = "NPV Counter Amount";
    col.field = "npvCounterAmt";
    col.cellClass = "blotter_amt_align";
    col.columnFilterType = ColumnFilterType.NONE;
    col.minWidth = 70;
    col.width = 120;
    col.comparator = this.createNumberComparator(col.field);
    return col;
  }

  private createFillDateCol() {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Fill Date";
    col.headerTooltip = "Fill Date";
    col.field = "fillDate";
    col.minWidth = 90;
    col.width = 130;
    col.hide = true;
    col.comparator = this.createDateComparator(col.field);
    return col;
  }

  private createValueDateCol(): BfxColDef {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Value Date";
    col.headerTooltip = "Value Date";
    col.field = "valueDt";
    col.minWidth = 90;
    col.width = 120;
    col.comparator = this.createDateComparator(col.field);
    return col;
  }

  private createTraderGroupCol(): BfxColDef {
    let col: BfxColDef = this.initColdDef();
    col.headerName = "Trader Group";
    col.headerTooltip = "Trader Group";
    col.field = "traderGroup";
    col.indexType = IndexType.String;
    col.minWidth = 100;
    col.width = 120;
    col.hide = true;

    if (!this.restoreFromConfig(col)) {
      col.position = this.position.next();
    }
    return col;
  }

  /**
   * Create generic number column, with no tooltip that pulls numeric values
   * from the rawValues for comparison
   * @param header
   * @param field
   * @returns
   */
  private createOrderRateCol(header: string, field: string) {
    let col: BfxColDef = this.initColdDef();
    col.headerName = header;
    col.headerTooltip = header;
    col.field = field;
    (col.cellClass = "blotter_amt_align"), (col.minWidth = 90);
    col.width = 90;
    col.comparator = this.createNumberComparator(col.field);
    return col;
  }

  /**
   * Create a comparator function that uses the numeric value from the rawValues
   * rather than the text value in the top-level allocation details
   * @param fieldId
   * @returns
   */
  private createNumberComparator(fieldId: string) {
    return (valueA: any, valueB: any, node1: RowNode, node2: RowNode, _isInverted: boolean) => {
      const alloc1 = node1.data;
      const alloc2 = node2.data;
      const amt1: CubiqTS.BigNumber = alloc1.rawValues?.[fieldId];
      const amt2: CubiqTS.BigNumber = alloc2.rawValues?.[fieldId];
      if (amt1 && amt2) {
        return amt1.comparedTo(amt2);
      }
      return amt1 ? 1 : amt2 ? -1 : 0;
    };
  }
  /**
   * Create a comparator function that uses the date value from the rawValues
   * rather than the text value in the top-level allocation details
   * @param fieldId
   * @returns
   */
  private createDateComparator(fieldId: string) {
    return (valueA: any, valueB: any, node1: RowNode, node2: RowNode, _isInverted: boolean) => {
      const alloc1 = node1.data;
      const alloc2 = node2.data;
      const date1: CubiqTS.Moment = alloc1.rawValues?.[fieldId];
      const date2: CubiqTS.Moment = alloc2.rawValues?.[fieldId];
      const date1Time = date1?.valueOf() ?? 0;
      const date2Time = date2?.valueOf() ?? 0;
      return date1Time - date2Time;
    };
  }

  public override getBlotterType(): BlotterType {
    return BlotterType.ALLOCATION;
  }

  public getVersionIdentifier(): string {
    return ALLOCATION_TABLE_DEFINITION_VER;
  }
}
